import z from "zod";
import moment from "moment";
import { membershipTemplateVisitsSchemaField } from "./membership_template";
import { addressFromJson, addressSchemaToJson } from "./address";
import { colors } from "assets/colors/colors";
import { fetchMembershipsPage } from "queries";
import { patientFromJson } from "./patient";

export const MEMBERSHIP_STATUS = {
  inactive: "inactive",
  active: "active",
  expired: "expired",
  displayValue: (status) => {
    if (status === MEMBERSHIP_STATUS.active) {
      return "Active";
    } else if (status === MEMBERSHIP_STATUS.inactive) {
      return "Inactive";
    } else if (status === MEMBERSHIP_STATUS.expired) {
      return "Expired";
    } else {
      return "Unknown";
    }
  },
  color: (status) => {
    if (status === MEMBERSHIP_STATUS.active) {
      return colors.success;
    } else if (status === MEMBERSHIP_STATUS.inactive) {
      return colors.redAlarm;
    } else if (status === MEMBERSHIP_STATUS.expired) {
      return colors.core60;
    }
  },
  values: () => [
    MEMBERSHIP_STATUS.active,
    MEMBERSHIP_STATUS.inactive,
    MEMBERSHIP_STATUS.expired,
  ],
};

export const MEMBERSHI_PATIENT_STATUS = {
  base: "base",
  primary: "primary",

  displayValue: (status) => {
    if (status === MEMBERSHI_PATIENT_STATUS.primary) {
      return "Primary";
    }
    return "";
  },
};

export const membershipNameSchemaField = (membershipUuid) =>
  z
    .string()
    .trim()
    .min(1, { message: "Membership name is required" })
    .max(100, { message: "Membership name must be less than 100 characters" })
    .refine(
      async (value) => {
        const memberships = (
          await fetchMembershipsPage({
            filters: { search: value },
            limit: 1,
          })
        ).results;
        if (
          memberships.length > 0 &&
          memberships[0].name.toLowerCase() === value.toLowerCase() &&
          memberships[0].uuid !== membershipUuid
        ) {
          return false;
        }
        return true;
      },
      { message: "Membership name must be unique" },
    );

export const membershipStartDateSchemaField = z.custom(
  (val) => val && moment.isMoment(val),
  {
    message: "Start date is required",
  },
);

export const membershipEndDateSchemaField = z.optional(
  z.custom((val) => val && moment.isMoment(val)),
);

export const membershipLinkedTemplateSchemaField = z
  .looseObject({})
  .nullable()
  .refine((val) => val, { message: "Template is required" });

export const membershipAddressSchemaField = z
  .looseObject({})
  .nullable()
  .refine((val) => val, { message: "Service address is required" });

export const membershipAmountSchemaField = z.preprocess(
  (value) => (typeof value === "number" ? value : -1),
  z.number().min(0, { message: "Default membership amount is required" }),
);

export const membershipVisitsSchemaField = z.array(
  z.object({
    uuid: z.string().optional(),
    visitType: z.preprocess(
      (val) => val || "",
      z.string().min(1, { message: "Visit type is required" }),
    ),
    comment: z.string().nullable(),
    price: z.preprocess(
      (value) => (typeof value === "number" ? value : -1),
      z.number().min(0, { message: "Price is required" }),
    ),
  }),
);

export const membershipsPeriodDisplayValue = ({ membership }) => {
  const startDate = membership.startDate?.format("MM/DD/YYYY");
  const endDate = membership.endDate?.format("MM/DD/YYYY");
  return `${startDate} - ${endDate}`;
};

export const membershipUsedVisitsSchemaField = z.array(
  z.object({
    uuid: z.string().optional(),
    comment: z.string().nullable(),
    price: z.preprocess(
      (value) => (typeof value === "number" ? value : -1),
      z.number().min(0, { message: "Price is required" }),
    ),
  }),
);
export const membershipSchemaToJsonTranform = (value) => {
  return {
    name: value.name,
    price: value.amount,
    start_date: value.startDate?.format("YYYY-MM-DD"),
    end_date: value.endDate?.format("YYYY-MM-DD"),
    membership_template_id: value.template?.value,
    membership_visits: value.visits?.map((visit) => ({
      uuid: visit.uuid,
      visit_type: visit.visitType,
      price: visit.price,
      comment:
        typeof visit.comment === "string" && visit.comment.length > 0
          ? visit.comment
          : null,
    })),
    service_address: value.address ? addressSchemaToJson(value.address) : null,
    membership_patients: value?.patients?.map((patient) => ({
      uuid: patient.uuid,
      member_status: value?.primaryPatientIds?.includes(patient.uuid)
        ? MEMBERSHI_PATIENT_STATUS.primary
        : MEMBERSHI_PATIENT_STATUS.base,
    })),
    used_membership_visits: value.usedVisits?.map((visit) => ({
      uuid: visit.uuid,
      comment:
        typeof visit.comment === "string" && visit.comment.length > 0
          ? visit.comment
          : null,
      price: visit.price,
    })),
  };
};

const sortByVisitName = (a, b) =>
  (a?.name || "").localeCompare(b?.name || "");

export const membershipFromJson = (json = {}) => {
  const startDate = moment(json.start_date);
  const endDate = moment(json.end_date);

  const sortedVisits = [...(json.membership_visits || [])].sort(
    sortByVisitName,
  );
  const sortedUsedVisits = [...(json.used_membership_visits || [])].sort(
    sortByVisitName,
  );
  return {
    uuid: json.uuid,
    startDate: startDate,
    endDate: endDate,
    name: json.name,
    address: json.service_address
      ? addressFromJson(json.service_address)
      : null,
    amount: json.price,
    status: json.status,
    patients: (json.membership_patients || []).map((patient) => ({
      ...patientFromJson(patient.patient || patient || {}),
      memberStatus:
        patient.member_status === MEMBERSHI_PATIENT_STATUS.primary
          ? MEMBERSHI_PATIENT_STATUS.primary
          : MEMBERSHI_PATIENT_STATUS.base,
    })),
    patientCount: json.patient_count,
    usedVisitCount: json.used_membership_visits_count,
    lastUsedVisitAt: json.last_used_membership_visit_at
      ? moment(json.last_used_membership_visit_at)
      : null,
    visit_last_used_at: json.membership_visit_last_used_at
      ? moment(json.membership_visit_last_used_at)
      : null,
    usedVisits: sortedUsedVisits.map((visit) => ({
      uuid: visit.uuid,
      usedAt: moment(visit.used_at),
      patientFullName: visit.patient_full_name,
      comment: visit.comment,
      name: visit.name,
      visitType: visit.visit_type,
      price: visit.price,
    })),
    visits: sortedVisits.map((visit) => ({
      uuid: visit.uuid,
      comment: visit.comment,
      visitType: visit.visit_type,
      price: visit.price,
      name: visit.name,
    })),
  };
};
